/**
 * Shared analytics helper functions for Job/Vehicle/Year Performance
 * and Pause-Time Analysis.
 *
 * RETIRED in K-T-12:
 * `calculateEfficiency` was the last inline KPI duplicate in the
 * metrics surface. Once K-T-10..K-T-12 routed every metrics-panel top
 * view AND drill-down through the canonical `kpi_*` RPC family, this
 * helper had zero remaining call sites and was removed. The canonical
 * formula `SUM(predicted_minutes) / SUM(actual_minutes)` now lives
 * exclusively in the SQL layer (see `supabase/migrations/202604301400
 * 00_kpi_technician_efficiency.sql` for the original definition).
 */

/**
 * Get the 5-year group label for a given year.
 * E.g., 2018 → "2016–2020", 2003 → "2001–2005"
 */
export function getYearGroup(year: number): string {
  // Calculate the start of the 5-year group (1991, 1996, 2001, etc.)
  const base = Math.floor((year - 1) / 5) * 5 + 1;
  return `${base}–${base + 4}`;
}

/**
 * Get all year group labels from a start year to an end year.
 */
export function getYearGroupRange(startYear: number, endYear: number): string[] {
  const groups: string[] = [];
  let year = startYear;
  while (year <= endYear) {
    const group = getYearGroup(year);
    if (!groups.includes(group)) {
      groups.push(group);
    }
    year += 5;
  }
  return groups;
}

/**
 * Parse a year group string back to start/end years.
 * E.g., "2016–2020" → { start: 2016, end: 2020 }
 */
export function parseYearGroup(group: string): { start: number; end: number } {
  const [start, end] = group.split('–').map(Number);
  return { start, end };
}

/**
 * Format minutes as hours and minutes string.
 * E.g., 125 → "2h 5m"
 */
export function formatMinutesAsTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format a decimal percentage nicely.
 * E.g., 0.85 → "85%", 1.23 → "123%"
 */
export function formatPercent(value: number | null, decimals = 0): string {
  if (value === null) return '—';
  return `${value.toFixed(decimals)}%`;
}
